import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Heart, CaretDown, CaretUp, CaretLeft, CaretRight, Plus, X, Check, Sparkle, MapPin } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WEDDING_EVENTS = new Set(['marriage', 'engagement', 'wedding', 'reception']);

const TRADITION_LABEL = {
  hindu: 'Hindu',
  muslim: 'Muslim',
  christian: 'Christian',
  brahmin: 'Brahmin',
};

// Normalize the customer-side marriageType ("hindu", "Hindu Wedding", etc.)
// to the lowercase tradition value the backend stores.
const normalizeTradition = (m) => {
  if (!m) return '';
  return String(m).toLowerCase().replace(/marriage|wedding|nikkah|\/|\s+/g, ' ').trim().split(' ')[0];
};

const PackageCard = ({ vendor, pkg, pkgIdx, inCart, finalPrice, photoIdx, onPrev, onNext, onJump, onToggleCart }) => {
  const photos = pkg.photos || [];
  const hasPhotos = photos.length > 0;
  const currentPhoto = hasPhotos ? photos[photoIdx % photos.length] : null;
  const photoSrc = currentPhoto && (currentPhoto.startsWith('http') ? currentPhoto : `${BACKEND_URL}${currentPhoto}`);
  const isWedding = WEDDING_EVENTS.has(pkg.event_type);

  return (
    <div
      className={`group relative border-2 rounded-lg overflow-hidden bg-white transition-all ${inCart ? 'border-green-500 ring-2 ring-green-200' : 'border-[#E8E1D5] hover:border-brand-primary/40'}`}
      data-testid={`decor-pkg-${vendor.id}-${pkgIdx}`}
    >
      {/* Photo carousel */}
      <div className="relative h-44 bg-sand-100 overflow-hidden">
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={pkg.name || vendor.business_name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-text-secondary text-xs">No photo</div>
        )}
        {/* Photo navigation */}
        {photos.length > 1 && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`decor-pkg-prev-${vendor.id}-${pkgIdx}`}
              aria-label="Previous photo"
            >
              <CaretLeft size={14} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`decor-pkg-next-${vendor.id}-${pkgIdx}`}
              aria-label="Next photo"
            >
              <CaretRight size={14} />
            </button>
            <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-medium">
              {(photoIdx % photos.length) + 1}/{photos.length}
            </div>
          </>
        )}
        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col items-start gap-1">
          <span className="text-[9px] tracking-wider uppercase font-bold bg-green-600 text-white px-1.5 py-0.5 rounded shadow">VERIFIED</span>
          {pkg.fresh_flowers && (
            <span className="text-[9px] tracking-wider uppercase font-bold bg-yellow-500 text-black px-1.5 py-0.5 rounded shadow flex items-center gap-0.5">
              <Sparkle size={9} weight="fill" /> FRESH FLOWERS
            </span>
          )}
        </div>
        {inCart && (
          <div className="absolute bottom-2 right-2 bg-green-500 text-white rounded-full p-1 shadow">
            <Check size={12} weight="bold" />
          </div>
        )}
        {/* Price ribbon */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
          <div className="font-heading text-xl font-bold text-white" data-testid={`decor-pkg-price-${vendor.id}-${pkgIdx}`}>
            ₹{finalPrice.toLocaleString('en-IN')}
          </div>
        </div>
      </div>

      {/* Photo thumbnails */}
      {photos.length > 1 && (
        <div className="flex gap-1 px-3 pt-2 overflow-x-auto">
          {photos.map((ph, i) => {
            const src = ph.startsWith('http') ? ph : `${BACKEND_URL}${ph}`;
            const active = i === (photoIdx % photos.length);
            return (
              <button
                type="button"
                key={i}
                onClick={(e) => { e.stopPropagation(); onJump(i); }}
                className={`flex-shrink-0 w-10 h-10 rounded border-2 overflow-hidden transition-all ${active ? 'border-brand-primary ring-1 ring-brand-primary' : 'border-[#E8E1D5] hover:border-brand-primary/40 opacity-70'}`}
                data-testid={`decor-pkg-thumb-${vendor.id}-${pkgIdx}-${i}`}
                aria-label={`Photo ${i + 1}`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.target.style.display = 'none'; }} />
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className="p-3 space-y-2">
        <div>
          <div className="font-heading text-base font-semibold text-text-primary truncate" data-testid={`decor-pkg-vendor-${vendor.id}-${pkgIdx}`}>
            {vendor.business_name || vendor.name || 'Decorator'}
          </div>
          {pkg.name && (
            <div className="text-xs text-text-secondary truncate">{pkg.name}</div>
          )}
          {vendor.location && (
            <div className="flex items-center gap-1 text-[11px] text-text-secondary mt-0.5">
              <MapPin size={10} /> <span className="truncate">{vendor.location}</span>
            </div>
          )}
        </div>

        {/* Chips */}
        {(isWedding && ((pkg.traditions && pkg.traditions.length) || (pkg.venue_types && pkg.venue_types.length))) ? (
          <div className="flex flex-wrap gap-1">
            {(pkg.traditions || []).map((t) => (
              <span key={t} className="text-[9px] bg-blue-100 text-blue-700 border border-blue-300 px-1.5 py-0.5 rounded font-medium uppercase tracking-wider">
                {TRADITION_LABEL[t] || t}
              </span>
            ))}
            {(pkg.venue_types || []).slice(0, 3).map((v) => (
              <span key={v} className="text-[9px] bg-sand-100 text-text-secondary border border-[#E8E1D5] px-1.5 py-0.5 rounded font-medium">
                {v}
              </span>
            ))}
          </div>
        ) : null}

        {/* Add button */}
        <Button
          type="button"
          size="sm"
          onClick={onToggleCart}
          className={`w-full rounded-none ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'} text-sand-50`}
          data-testid={`decor-pkg-toggle-${vendor.id}-${pkgIdx}`}
        >
          {inCart ? (<><X size={14} className="mr-1" /> Remove</>) : (<><Plus size={14} className="mr-1" /> Add to Estimate</>)}
        </Button>
      </div>
    </div>
  );
};

/**
 * Customer-side Decoration panel.
 *
 * Replaces the old static "Indoor / Outdoor / Lightings / Flower / House
 * Decoration" sub-category grid. Fetches real `decorator_packages` authored
 * by verified decorator vendors through the vendor Quick Add menu and
 * renders them as add-to-estimate cards.
 *
 * Wedding events (marriage / engagement) auto-filter by the customer's
 * selected tradition and offer optional venue-type + fresh-flowers chips.
 * Non-wedding events skip those filters entirely.
 */
const DecoratorPanel = ({
  eventType,
  marriageType,
  cart,
  setCart,
  markup,
  expanded,
  onToggle,
}) => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [venueFilter, setVenueFilter] = useState('all');
  const [photoIdxMap, setPhotoIdxMap] = useState({}); // { 'vid:pkgIdx': activeIdx }

  const isWedding = WEDDING_EVENTS.has(eventType);
  const tradition = isWedding ? normalizeTradition(marriageType) : '';

  useEffect(() => {
    if (!eventType) {
      setVendors([]);
      return;
    }
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('event_type', eventType);
        if (isWedding && tradition) params.append('tradition', tradition);
        const { data } = await axios.get(`${BACKEND_URL}/api/instant-pricing/decorators?${params.toString()}`);
        setVendors(Array.isArray(data) ? data : []);
      } catch {
        setVendors([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // Reset filters when event changes
    setVenueFilter('all');
  }, [eventType, tradition, isWedding]);

  // All available venue types from the loaded packages (for wedding filter chips)
  const venueOptions = useMemo(() => {
    if (!isWedding) return [];
    const set = new Set();
    vendors.forEach((v) => {
      (v.packages || []).forEach((p) => {
        (p.venue_types || []).forEach((vt) => set.add(vt));
      });
    });
    return Array.from(set);
  }, [vendors, isWedding]);

  // Flatten + split into two segments: fresh-flowers vs standard
  const { freshCards, standardCards } = useMemo(() => {
    const fresh = [];
    const standard = [];
    vendors.forEach((v) => {
      (v.packages || []).forEach((pkg, pkgIdx) => {
        // Apply venue filter
        if (isWedding && venueFilter !== 'all') {
          if (!(pkg.venue_types || []).includes(venueFilter)) return;
        }
        const entry = { vendor: v, pkg, pkgIdx };
        if (pkg.fresh_flowers) fresh.push(entry);
        else standard.push(entry);
      });
    });
    // Sort each segment by price ascending
    fresh.sort((a, b) => (a.pkg.price || 0) - (b.pkg.price || 0));
    standard.sort((a, b) => (a.pkg.price || 0) - (b.pkg.price || 0));
    return { freshCards: fresh, standardCards: standard };
  }, [vendors, isWedding, venueFilter]);

  const totalVendors = vendors.length;
  const totalPackages = freshCards.length + standardCards.length;

  // Shared card renderer used by both segments
  const renderCard = ({ vendor, pkg, pkgIdx }) => {
    const cartId = `decor-real-${vendor.id}-${pkgIdx}`;
    const inCart = !!cart.find((c) => c.id === cartId);
    const finalPrice = markup(pkg.price || 0, 'vendor');
    const key = `${vendor.id}:${pkgIdx}`;
    const photoIdx = photoIdxMap[key] || 0;
    const total = (pkg.photos || []).length;
    const onPrev = () => setPhotoIdxMap((m) => ({ ...m, [key]: (photoIdx - 1 + total) % Math.max(total, 1) }));
    const onNext = () => setPhotoIdxMap((m) => ({ ...m, [key]: (photoIdx + 1) % Math.max(total, 1) }));
    const onJump = (i) => setPhotoIdxMap((m) => ({ ...m, [key]: i }));
    const onToggleCart = () => {
      if (inCart) {
        setCart(cart.filter((c) => c.id !== cartId));
      } else {
        setCart([
          ...cart,
          {
            id: cartId,
            name: `${vendor.business_name || vendor.name || 'Decorator'}${pkg.name ? ` — ${pkg.name}` : ''}`,
            category: 'Decoration',
            price: finalPrice,
            image: (Array.isArray(pkg.photos) && pkg.photos[0]) || pkg.photo || null,
          },
        ]);
      }
    };
    return (
      <PackageCard
        key={cartId}
        vendor={vendor}
        pkg={pkg}
        pkgIdx={pkgIdx}
        inCart={inCart}
        finalPrice={finalPrice}
        photoIdx={photoIdx}
        onPrev={onPrev}
        onNext={onNext}
        onJump={onJump}
        onToggleCart={onToggleCart}
      />
    );
  };

  if (!eventType) return null;

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="pricing-decorator">
      <CardHeader className="cursor-pointer" onClick={onToggle}>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Heart size={20} className="text-brand-primary" /> Decoration
            {totalVendors > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 bg-green-600 text-white rounded font-bold uppercase tracking-wider" data-testid="decor-verified-count">
                {totalVendors} Verified
              </span>
            )}
          </CardTitle>
          {expanded ? <CaretUp size={18} /> : <CaretDown size={18} />}
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4">
          {/* Wedding venue filter (fresh flowers now lives in its own segment below) */}
          {isWedding && (totalVendors > 0 || loading) && venueOptions.length > 0 && (
            <div className="space-y-3 pb-2 border-b border-[#E8E1D5]">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-text-secondary mb-2">Venue Type</div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setVenueFilter('all')}
                    className={`text-xs px-3 py-1 rounded-full border transition-all ${venueFilter === 'all' ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'bg-white text-text-secondary border-[#E8E1D5] hover:border-brand-primary/40'}`}
                    data-testid="decor-venue-filter-all"
                  >
                    All
                  </button>
                  {venueOptions.map((vt) => (
                    <button
                      type="button"
                      key={vt}
                      onClick={() => setVenueFilter(vt)}
                      className={`text-xs px-3 py-1 rounded-full border transition-all ${venueFilter === vt ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'bg-white text-text-secondary border-[#E8E1D5] hover:border-brand-primary/40'}`}
                      data-testid={`decor-venue-filter-${vt.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                    >
                      {vt}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="text-center py-10 text-text-secondary text-sm" data-testid="decor-loading">
              Loading verified decorators…
            </div>
          )}

          {/* Empty state */}
          {!loading && totalVendors === 0 && (
            <div className="text-center py-10 border border-dashed border-[#E8E1D5] rounded" data-testid="decor-empty">
              <Heart size={32} className="text-text-secondary/40 mx-auto mb-2" />
              <p className="text-sm text-text-secondary">No verified decorators yet for this event</p>
              <p className="text-xs text-text-secondary mt-1">
                Real decorator vendors will appear here once they list packages for{' '}
                <span className="font-medium">
                  {isWedding && tradition ? `${TRADITION_LABEL[tradition] || tradition} ` : ''}
                  {eventType.replace(/_/g, ' ')}
                </span>.
              </p>
            </div>
          )}

          {/* Filtered to zero */}
          {!loading && totalVendors > 0 && totalPackages === 0 && (
            <div className="text-center py-8 border border-dashed border-[#E8E1D5] rounded" data-testid="decor-no-match">
              <p className="text-sm text-text-secondary">No packages match your selected venue</p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setVenueFilter('all')}
                className="mt-2 rounded-none border-[#E8E1D5]"
                data-testid="decor-clear-filters"
              >
                Clear filters
              </Button>
            </div>
          )}

          {/* SEGMENT 1: Fresh Flowers Included — shown ABOVE standard packages */}
          {!loading && freshCards.length > 0 && (
            <div className="space-y-3" data-testid="decor-segment-fresh">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-gradient-to-r from-yellow-400 to-pink-400 text-white px-3 py-1 rounded-full shadow-sm">
                  <Sparkle size={14} weight="fill" />
                  <span className="text-xs font-bold uppercase tracking-wider">Fresh Flowers Included</span>
                </div>
                <span className="text-xs text-text-secondary" data-testid="decor-fresh-count">
                  {freshCards.length} package{freshCards.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="decor-fresh-grid">
                {freshCards.map(renderCard)}
              </div>
            </div>
          )}

          {/* Divider between segments when both exist */}
          {!loading && freshCards.length > 0 && standardCards.length > 0 && (
            <div className="border-t border-[#E8E1D5]" />
          )}

          {/* SEGMENT 2: Standard packages (no fresh flowers flag) */}
          {!loading && standardCards.length > 0 && (
            <div className="space-y-3" data-testid="decor-segment-standard">
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 bg-text-primary text-sand-50 px-3 py-1 rounded-full">
                  <Heart size={14} />
                  <span className="text-xs font-bold uppercase tracking-wider">Decoration Packages</span>
                </div>
                <span className="text-xs text-text-secondary" data-testid="decor-standard-count">
                  {standardCards.length} package{standardCards.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="decor-standard-grid">
                {standardCards.map(renderCard)}
              </div>
            </div>
          )}

          {!loading && totalPackages > 0 && (
            <p className="text-[10px] text-text-secondary text-center italic pt-1">
              *Prices include platform fees
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default DecoratorPanel;
